import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";
import { getPriceHistory } from "./StockPriceService";
import { sma, ema, rsi, macd } from "./StockIndicators";

const RANGES = [
  { key: "1M", days: 30 },
  { key: "3M", days: 90 },
  { key: "6M", days: 182 },
  { key: "1Y", days: 365 },
  { key: "ALL", days: null },
];

const INDICATORS = [
  { key: "SMA20", label: "SMA 20", color: "#f59e0b" },
  { key: "SMA50", label: "SMA 50", color: "#3b82f6" },
  { key: "EMA20", label: "EMA 20", color: "#8b5cf6" },
  { key: "RSI14", label: "RSI 14", color: "#6366f1" },
  { key: "MACD", label: "MACD", color: "#22c55e" },
];

export default function StockChart({ symbol, stockName, onClose }) {
  const [history, setHistory] = useState({ points: [], stockName: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState("3M");
  const [indicators, setIndicators] = useState(() => new Set(["SMA20"]));

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    getPriceHistory(symbol)
      .then((data) => setHistory(data || { points: [] }))
      .catch((e) => setError(e.message || "Failed to load price history"))
      .finally(() => setLoading(false));
  }, [symbol]);

  const toggleIndicator = (key) => {
    setIndicators((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const points = useMemo(() => history?.points || [], [history]);

  const filteredPoints = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    if (!r || r.days === null) return points;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - r.days);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return points.filter((p) => p.priceDate >= cutoffStr);
  }, [points, range]);

  const chartData = useMemo(() => {
    const prices = filteredPoints.map((p) => Number(p.price));
    const sma20 = sma(prices, 20);
    const sma50 = sma(prices, 50);
    const ema20 = ema(prices, 20);
    const rsi14 = rsi(prices, 14);
    const macdResult = macd(prices);
    return filteredPoints.map((p, i) => ({
      date: p.priceDate,
      price: Number(p.price),
      sma20: sma20[i],
      sma50: sma50[i],
      ema20: ema20[i],
      rsi14: rsi14[i],
      macdLine: macdResult.macd[i],
      macdSignal: macdResult.signal[i],
    }));
  }, [filteredPoints]);

  const latest = filteredPoints[filteredPoints.length - 1];
  const previous = filteredPoints[filteredPoints.length - 2];
  const changePct = latest && previous
    ? ((Number(latest.price) - Number(previous.price)) / Number(previous.price)) * 100
    : null;

  const priceCount = filteredPoints.length;

  return (
    <div className="card">
      <div className="card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>
          {symbol} {stockName || history.stockName ? `· ${stockName || history.stockName}` : ""}
        </span>
        {onClose && (
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        )}
      </div>

      {loading && <div>Loading price history…</div>}
      {error && <div className="stocks-bad">{error}</div>}

      {!loading && !error && (
        <>
          {latest && (
            <div style={{ marginBottom: 12 }}>
              <span style={{ fontSize: 22, fontWeight: 700 }}>₹{Number(latest.price).toFixed(2)}</span>
              {changePct !== null && (
                <span className={changePct >= 0 ? "stocks-good" : "stocks-bad"} style={{ marginLeft: 10, fontWeight: 600 }}>
                  {changePct >= 0 ? "▲" : "▼"} {Math.abs(changePct).toFixed(2)}%
                </span>
              )}
              <span style={{ marginLeft: 10, fontSize: 12, color: "var(--color-muted)" }}>as of {latest.priceDate}</span>
            </div>
          )}

          <div className="stocks-tabs">
            {RANGES.map((r) => (
              <button
                key={r.key}
                className={`tab-btn btn-sm ${range === r.key ? "active" : ""}`}
                onClick={() => setRange(r.key)}
              >
                {r.key}
              </button>
            ))}
          </div>

          {priceCount >= 2 ? (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="price" name="Price" stroke="#22c55e" dot={false} strokeWidth={2} />
                {indicators.has("SMA20") && (
                  <Line type="monotone" dataKey="sma20" name="SMA 20" stroke="#f59e0b" dot={false} strokeWidth={1.5} />
                )}
                {indicators.has("SMA50") && (
                  <Line type="monotone" dataKey="sma50" name="SMA 50" stroke="#3b82f6" dot={false} strokeWidth={1.5} />
                )}
                {indicators.has("EMA20") && (
                  <Line type="monotone" dataKey="ema20" name="EMA 20" stroke="#8b5cf6" dot={false} strokeWidth={1.5} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: "var(--color-muted)", padding: 12 }}>
              Need at least 2 price points in this range to draw a chart.
            </div>
          )}

          <div className="stocks-tabs">
            {INDICATORS.map((ind) => (
              <button
                key={ind.key}
                className={`tab-btn btn-sm ${indicators.has(ind.key) ? "active" : ""}`}
                onClick={() => toggleIndicator(ind.key)}
              >
                {ind.label}
              </button>
            ))}
          </div>

          {indicators.has("RSI14") && (
            <div style={{ marginTop: 12 }}>
              <div className="card-title" style={{ fontSize: 13 }}>RSI (14)</div>
              {priceCount > 14 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="rsi14" name="RSI 14" stroke="#6366f1" dot={false} strokeWidth={2} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: "var(--color-muted)" }}>Need at least 15 price points to compute RSI (have {priceCount}).</div>
              )}
            </div>
          )}

          {indicators.has("MACD") && (
            <div style={{ marginTop: 12 }}>
              <div className="card-title" style={{ fontSize: 13 }}>MACD (12, 26, 9)</div>
              {priceCount > 26 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="macdLine" name="MACD" stroke="#22c55e" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="macdSignal" name="Signal" stroke="#ef4444" dot={false} strokeWidth={1.5} />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: "var(--color-muted)" }}>Need at least 27 price points to compute MACD (have {priceCount}).</div>
              )}
            </div>
          )}

          {points.length === 0 && (
            <div style={{ color: "var(--color-muted)", marginTop: 12 }}>No price history available yet for {symbol}.</div>
          )}
        </>
      )}
    </div>
  );
}
